import React, { createContext, useCallback, useContext, useMemo } from "react";
import { buildOrgPath } from "@constants/Paths";
const ActiveOrganizationContext = createContext(undefined);
/**
 * Provides the organization the `/o/:orgId` segment resolved to. Mounted only by
 * `ActiveOrgLayout` once the segment has been authorized against the memberships the
 * signed-in user holds, so consumers can rely on `activeOrg` being one the user is in.
 */
export const ActiveOrganizationProvider = ({ activeOrgId, activeOrg, memberships, children, }) => {
    const contextValue = useMemo(() => ({ activeOrgId, activeOrg, memberships }), [activeOrgId, activeOrg, memberships]);
    return React.createElement(ActiveOrganizationContext.Provider, { value: contextValue }, children);
};
export const useActiveOrganization = () => {
    const context = useContext(ActiveOrganizationContext);
    if (context === undefined) {
        throw new Error("useActiveOrganization must be used within an ActiveOrganizationProvider");
    }
    return context;
};
/**
 * Non-throwing variant for surfaces shared with staff or rendered outside the org segment
 * (e.g. Restricted), which may mount without a provider.
 */
export const useOptionalActiveOrganization = () => useContext(ActiveOrganizationContext);
/**
 * Builds a navigation target that is correct for whichever audience is rendering the call site.
 *
 * Member pages are mounted twice: once under `/o/:orgId` (org segment, provider present) and once
 * at their legacy unprefixed path, which is the *canonical* URL for staff — staff are not
 * org-scoped, so they never enter the segment (ActiveOrgLayout redirects them out of it) and never
 * have a provider above them. Inside the segment this prefixes the path with the resolved org;
 * outside it the path is returned untouched, which is exactly the URL staff should stay on.
 *
 * This is why those pages must not call the throwing `useActiveOrganization()`: for staff there is
 * legitimately no active org, and throwing turns a supported audience into a crash.
 */
export const useOrgPath = () => {
    var _a;
    const activeOrgId = (_a = useOptionalActiveOrganization()) === null || _a === void 0 ? void 0 : _a.activeOrgId;
    return useCallback((path) => (activeOrgId ? buildOrgPath(activeOrgId, path) : String(path)), [activeOrgId]);
};
